import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { createVideoMetadata, countListValues } from '@jwp/ott-common/src/utils/metadata';
import PlayTrailer from '@jwp/ott-theme/assets/icons/play_trailer.svg?react';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import useMovieData from '@jwp/ott-hooks-react/src/useMovieData';
import useMovieParams from '@jwp/ott-ui-react/src/hooks/useMovieParams';
import useMovieHandlers from '@jwp/ott-ui-react/src/hooks/useMovieHandlers';
import useAutoFocus from '@jwp/ott-ui-react/src/hooks/useAutoFocus';
import type { ScreenComponent } from '@jwp/ott-ui-react/types/screens';
import Cinema from '@jwp/ott-ui-react/src/containers/Cinema/Cinema';
import TrailerModal from '@jwp/ott-ui-react/src/containers/TrailerModal/TrailerModal';
import ShareButton from '@jwp/ott-ui-react/src/components/ShareButton/ShareButton';
import FavoriteButton from '@jwp/ott-ui-react/src/containers/FavoriteButton/FavoriteButton';
import Button from '@jwp/ott-ui-react/src/components/Button/Button';
import Icon from '@jwp/ott-ui-react/src/components/Icon/Icon';
import VideoMetaData from '@jwp/ott-ui-react/src/components/VideoMetaData/VideoMetaData';
import VideoTags from '@jwp/ott-ui-react/src/components/VideoTags/VideoTags';
import VideoDefinitionList from '@jwp/ott-ui-react/src/components/VideoDefintionsList/VideoDefinitionList';
import MediaHead from '@jwp/ott-ui-react/src/components/MediaHead/MediaHead';
import MediaHeroInfo from '@jwp/ott-ui-react/src/components/MediaHeroInfo/MediaHeroInfo';
import MediaHeroButtons from '@jwp/ott-ui-react/src/components/MediaHeroButtons/MediaHeroButtons';
import MediaHero from '@jwp/ott-ui-react/src/components/MediaHero/MediaHero';
import MediaTabs from '@jwp/ott-ui-react/src/components/MediaTabs/MediaTabs';
import MediaTabAbout from '@jwp/ott-ui-react/src/components/MediaTabAbout/MediaTabAbout';
import { formatItemDate } from '@jwp/ott-common/src/utils/datetime';
import StartWatchingButton from '@jwp/ott-ui-react/src/containers/StartWatchingButton/StartWatchingButton';
import env from '@jwp/ott-common/src/env';
import MarkdownComponent from '@jwp/ott-ui-react/src/components/MarkdownComponent/MarkdownComponent';
import TruncatedText from '@jwp/ott-ui-react/src/components/TruncatedText/TruncatedText';
import CardGrid from '@jwp/ott-ui-react/src/components/CardGrid/CardGrid';
import styles from '@jwp/ott-ui-react/src/pages/ScreenRouting/mediaScreens/MediaMovie/MediaMovie.module.scss';
import { generateMovieJSONLD } from '@jwp/ott-common/src/utils/structuredData';

const MediaConcert: ScreenComponent<PlaylistItem> = ({ data, isLoading }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('video');
  const { id, play, feedId } = useMovieParams();
  const {
    accessModel,
    isLoggedIn,
    hasSubscription,
    passes,
    playlist,
    playlistTitle,
    nextItem,
    playUrl,
    movieURL,
    trailerItem,
    hasTrailer,
    isFavoritesEnabled,
    isEntitled,
    playTrailer,
    isPlaylistLoading,
    videoFormats,
    setPlayTrailer,
    getCardUrl,
  } = useMovieData(data, id, feedId);
  const { handleBack, handleComplete } = useMovieHandlers(nextItem, movieURL, playlist);

  const structuredData = generateMovieJSONLD(data, env.APP_PUBLIC_URL);
  const canonicalUrl = data ? `${env.APP_PUBLIC_URL}${mediaURL({ id: data.mediaid, title: data.title })}` : window.location.href;

  const customDate = formatItemDate(data.date as string | undefined, language);
  const customMetadata = [
    ...createVideoMetadata(
      { ...data, pubdate: 0 },
      {
        hoursAbbreviation: t('common:abbreviation.hours'),
        minutesAbbreviation: t('common:abbreviation.minutes'),
      },
    ),
    ...(customDate ? [customDate] : []),
    ...(data?.contentType ? [data.contentType] : []),
  ];

  useAutoFocus(id);

  return (
    <React.Fragment>
      <MediaHead canonicalUrl={canonicalUrl} data={data} structuredData={structuredData} />
      <MediaHero image={data.backgroundImage}>
        <MediaHeroInfo
          title={data.title}
          description={data.synopsis ? (data.synopsis as string) : <TruncatedText text={data.description} maximumLines={2} className={styles.description} />}
          metadataComponent={<VideoMetaData attributes={customMetadata} />}
          tagsComponent={<VideoTags attributes={videoFormats} />}
          definitionComponent={
            <VideoDefinitionList
              splitByComma={false}
              listProperties={[
                {
                  label: t('video:metadata.orchestra', { count: countListValues(data?.orchestra as string) }),
                  value: data?.orchestra as string,
                },
                {
                  label: t('video:metadata.location'),
                  value: data?.location as string, // WMC-specific
                },
                {
                  label: t('video:metadata.venue'),
                  value: data?.venue as string,
                },
                {
                  label: t('video:metadata.conductor', { count: countListValues(data?.conductor as string) }),
                  value: data?.conductor as string,
                },
              ]}
            />
          }
        />
        <MediaHeroButtons
          cta={
            <StartWatchingButton
              key={id} // necessary to fix autofocus on TalkBack
              item={data}
              playUrl={playUrl}
            />
          }
        >
          {hasTrailer ? (
            <Button
              label={t('video:trailer')}
              aria-label={t('video:watch_trailer')}
              startIcon={<Icon icon={PlayTrailer} />}
              onClick={() => setPlayTrailer(true)}
              active={playTrailer}
              disabled={!trailerItem}
            />
          ) : undefined}
          {isFavoritesEnabled ? <FavoriteButton item={data} /> : undefined}
          <ShareButton title={data.title} description={data.description} url={canonicalUrl} />
        </MediaHeroButtons>
      </MediaHero>
      <MediaTabs titles={[playlistTitle, t('video:about_tab')]} isLoading={isLoading || isPlaylistLoading}>
        {playlist && (
          <CardGrid
            playlist={playlist}
            isLoading={isLoading || isPlaylistLoading}
            accessModel={accessModel}
            isLoggedIn={isLoggedIn}
            currentCardItem={data}
            currentCardLabel={t('video:current_video')}
            hasSubscription={hasSubscription}
            passes={passes}
            getUrl={getCardUrl}
          />
        )}
        <MediaTabAbout description={<MarkdownComponent className={styles.markdown} markdownString={data.description} headerOffset={1} />}>
          <div className={styles.aboutDefinitionList}>
            <VideoDefinitionList
              className={styles.definitionList}
              listProperties={[
                {
                  label: t('video:metadata.genres', { count: countListValues(data?.genre as string) }),
                  value: data?.genre as string,
                },
                {
                  label: t('video:metadata.composer', { count: countListValues(data?.composer as string) }),
                  value: data?.composer as string,
                },
                {
                  label: t('video:metadata.solist', { count: countListValues(data?.solist as string) }),
                  value: data?.solist as string, // WMC-specific
                },
                {
                  label: t('video:metadata.directors', { count: countListValues(data?.videoDirector as string) }),
                  value: data?.videoDirector as string,
                },
              ]}
            />
            <VideoDefinitionList
              className={styles.definitionList}
              listProperties={[
                { label: t('video:metadata.period'), value: data?.period as string },
                { label: t('video:metadata.division'), value: data?.division as string }, // WMC-specific
                { label: t('video:metadata.arranger'), value: data?.arranger as string }, // WMC-specific
                { label: t('video:metadata.producer'), value: data?.producer as string },
              ]}
            />
          </div>
          {data.copyright ? <p>{`© ${data.copyright}. All rights reserved`}</p> : null}
        </MediaTabAbout>
      </MediaTabs>
      <Cinema
        open={play && isEntitled}
        onClose={handleBack}
        item={data}
        title={data.title}
        primaryMetadata={<VideoMetaData attributes={customMetadata} />}
        onComplete={handleComplete}
        feedId={feedId ?? undefined}
        onNext={handleComplete}
      />
      <TrailerModal item={trailerItem} title={`${data.title} - Trailer`} open={playTrailer} onClose={() => setPlayTrailer(false)} />
    </React.Fragment>
  );
};

export default MediaConcert;
